/* Lost Boy homepage — carousel arrows + scroll progress */
if (!customElements.get('lb-carousel')) {
  class LbCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.lb-carousel__track');
      this.bar = this.querySelector('.lb-progress__bar');
      this.progress = this.querySelector('.lb-progress');
      if (!this.track) return;

      // Smoothness is handled by animateTo(); native smooth scroll fights
      // the manual tween and the drag interaction, so force instant scrolling.
      this.track.style.scrollBehavior = 'auto';

      this.querySelectorAll('[data-lb-prev]').forEach((btn) =>
        btn.addEventListener('click', () => this.scrollByDir(-1))
      );
      this.querySelectorAll('[data-lb-next]').forEach((btn) =>
        btn.addEventListener('click', () => this.scrollByDir(1))
      );

      if (this.bar) {
        this.track.addEventListener('scroll', () => this.updateProgress(), { passive: true });
        window.addEventListener('resize', () => this.updateProgress());
        if (this.progress) this.setupScrollbarDrag();
        this.updateProgress();
      }
    }

    scrollByDir(dir) {
      const card = this.track.querySelector(':scope > *');
      const step = card ? (card.offsetWidth + 24) * 2 : this.track.clientWidth * 0.8;
      this.animateTo(this.track.scrollLeft + dir * step);
    }

    // Turn the progress bar into a real scrollbar: click or drag it to scroll the track.
    setupScrollbarDrag() {
      let dragging = false;
      const scrollToPointer = (clientX) => {
        const rect = this.progress.getBoundingClientRect();
        const frac = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
        const max = this.track.scrollWidth - this.track.clientWidth;
        this.track.scrollLeft = frac * max;
        this.updateProgress();
      };
      this.progress.addEventListener('pointerdown', (e) => {
        dragging = true;
        try { this.progress.setPointerCapture(e.pointerId); } catch (_) {}
        scrollToPointer(e.clientX);
        e.preventDefault();
      });
      this.progress.addEventListener('pointermove', (e) => {
        if (dragging) scrollToPointer(e.clientX);
      });
      const end = () => { dragging = false; };
      this.progress.addEventListener('pointerup', end);
      this.progress.addEventListener('pointercancel', end);
    }

    // Native smooth scrollBy silently no-ops in some embedded browsers, so tween manually,
    // with an instant-jump fallback for environments where rAF never fires.
    animateTo(target) {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const to = Math.max(0, Math.min(max, target));
      const from = this.track.scrollLeft;
      const start = performance.now();
      const duration = 420;
      let ticked = false;
      cancelAnimationFrame(this.raf);
      clearTimeout(this.rafWatchdog);
      const tick = (now) => {
        ticked = true;
        const t = Math.min(1, (now - start) / duration);
        const ease = 1 - Math.pow(1 - t, 3);
        this.track.scrollLeft = from + (to - from) * ease;
        if (t < 1) this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
      this.rafWatchdog = setTimeout(() => {
        if (!ticked) {
          cancelAnimationFrame(this.raf);
          this.track.scrollLeft = to;
        }
      }, 120);
    }

    updateProgress() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (max <= 0) {
        this.bar.style.width = '100%';
        this.bar.style.marginInlineStart = '0%';
        return;
      }
      // Thumb width reflects how much of the track is visible; it slides across
      // the remaining space as you scroll — a true scrollbar thumb.
      const ratio = this.track.clientWidth / this.track.scrollWidth;
      const thumb = Math.max(14, Math.min(100, ratio * 100));
      const pos = this.track.scrollLeft / max;
      this.bar.style.width = `${thumb}%`;
      this.bar.style.marginInlineStart = `${pos * (100 - thumb)}%`;
    }
  }
  customElements.define('lb-carousel', LbCarousel);
}

/* Header — mobile drawer toggle */
if (!customElements.get('lb-header')) {
  class LbHeader extends HTMLElement {
    connectedCallback() {
      this.toggleBtn = this.querySelector('[data-lb-menu-toggle]');
      this.drawer = this.querySelector('[data-lb-drawer]');
      if (!this.toggleBtn) return;

      this.toggleBtn.addEventListener('click', () => this.toggle());
      this.querySelectorAll('[data-lb-drawer] a').forEach((a) =>
        a.addEventListener('click', () => this.close())
      );
      const overlay = this.querySelector('[data-lb-overlay]');
      if (overlay) overlay.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }

    toggle() {
      this.classList.contains('is-open') ? this.close() : this.open();
    }
    open() {
      this.classList.add('is-open');
      this.toggleBtn.setAttribute('aria-expanded', 'true');
    }
    close() {
      this.classList.remove('is-open');
      this.toggleBtn.setAttribute('aria-expanded', 'false');
    }
  }
  customElements.define('lb-header', LbHeader);
}

/* Hero carousel — auto-advancing slideshow with arrows, dots, swipe */
if (!customElements.get('lb-hero-carousel')) {
  class LbHeroCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.lb-hc__track');
      this.slides = Array.from(this.querySelectorAll('.lb-hc__slide'));
      this.dots = Array.from(this.querySelectorAll('[data-lb-hc-dot]'));
      if (this.slides.length < 2) return;

      this.index = 0;
      this.fade = this.dataset.transition === 'fade';
      this.speed = parseInt(this.dataset.speed, 10) || 6000;
      this.autoplay = this.dataset.autoplay === 'true';

      this.querySelectorAll('[data-lb-hc-prev]').forEach((b) => b.addEventListener('click', () => this.step(-1, true)));
      this.querySelectorAll('[data-lb-hc-next]').forEach((b) => b.addEventListener('click', () => this.step(1, true)));
      this.dots.forEach((dot) =>
        dot.addEventListener('click', () => this.goTo(parseInt(dot.dataset.lbHcDot, 10), true))
      );

      this.setupSwipe();

      // Pause on hover/focus so a reader isn't yanked to the next slide.
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
      document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.start()));

      this.render();
      this.start();
    }

    disconnectedCallback() {
      this.stop();
    }

    goTo(i, userInitiated) {
      const n = this.slides.length;
      this.index = ((i % n) + n) % n;
      this.render();
      if (userInitiated) this.restart();
    }

    step(dir, userInitiated) {
      this.goTo(this.index + dir, userInitiated);
    }

    render() {
      if (this.fade) {
        this.slides.forEach((s, i) => s.classList.toggle('is-active', i === this.index));
      } else if (this.track) {
        this.track.style.transform = `translateX(-${this.index * 100}%)`;
        this.slides.forEach((s, i) => s.classList.toggle('is-active', i === this.index));
      }
      this.dots.forEach((d, i) => {
        const on = i === this.index;
        d.classList.toggle('is-active', on);
        d.setAttribute('aria-selected', on ? 'true' : 'false');
      });
    }

    start() {
      if (!this.autoplay || this.timer || this.slides.length < 2) return;
      this.timer = setInterval(() => this.step(1, false), this.speed);
    }

    stop() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    }

    restart() {
      this.stop();
      this.start();
    }

    setupSwipe() {
      const vp = this.querySelector('.lb-hc__viewport');
      if (!vp) return;
      let x0 = null;
      vp.addEventListener(
        'pointerdown',
        (e) => {
          x0 = e.clientX;
          this.stop();
        },
        { passive: true }
      );
      const end = (e) => {
        if (x0 === null) return;
        const dx = e.clientX - x0;
        x0 = null;
        if (Math.abs(dx) > 40) this.step(dx < 0 ? 1 : -1, true);
        else this.start();
      };
      vp.addEventListener('pointerup', end, { passive: true });
      vp.addEventListener('pointercancel', () => {
        x0 = null;
        this.start();
      });
    }
  }
  customElements.define('lb-hero-carousel', LbHeroCarousel);
}

/* Contact — topic picker swaps in the matching form */
if (!customElements.get('lb-contact-topics')) {
  class LbContactTopics extends HTMLElement {
    connectedCallback() {
      this.STORE_KEY = 'lb-contact-topic';
      this.tabs = Array.from(this.querySelectorAll('[role="tab"][data-topic]'));
      this.panels = Array.from(this.querySelectorAll('.lb-topic-panel'));
      if (!this.tabs.length) return;

      /* Shopify flags EVERY contact form on the page as posted after any submit,
         so every panel renders a success banner with its fields hidden. We keep
         that banner only on the panel that was actually submitted (see
         reconcilePosted) and reset the rest to a clean, sendable form. */
      this.posted = !!this.querySelector('[data-lb-success]');

      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab.dataset.topic, true));
        tab.addEventListener('keydown', (e) => this.onKeydown(e));
      });

      this.querySelectorAll('form').forEach((form) => {
        form.addEventListener('submit', () => {
          const panel = form.closest('.lb-topic-panel');
          if (!panel) return;
          try {
            sessionStorage.setItem(this.STORE_KEY, panel.dataset.topic);
          } catch (e) {
            /* private mode — fall back to the first topic */
          }
        });
      });

      const initial = this.initialTopic();
      this.select(initial, false);

      if (this.posted) {
        this.reconcilePosted(initial);
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        try {
          sessionStorage.removeItem(this.STORE_KEY);
        } catch (e) {
          /* nothing to clean up */
        }
      }
    }

    /* Keep the success banner on the submitted panel only; every other panel
       gets its banner removed and its fields un-hidden so switching topics (or
       clicking "Send another message") always lands on a usable form. */
    reconcilePosted(activeTopic) {
      this.panels.forEach((panel) => {
        const success = panel.querySelector('[data-lb-success]');
        const fields = panel.querySelector('[data-lb-fields]');

        if (panel.dataset.topic === activeTopic) {
          const again = panel.querySelector('[data-lb-send-another]');
          if (again) {
            again.addEventListener('click', () => {
              if (success) success.remove();
              if (fields) {
                fields.hidden = false;
                const first = fields.querySelector('input:not([type=hidden]), textarea, select');
                if (first) first.focus();
              }
            });
          }
        } else {
          if (success) success.remove();
          if (fields) fields.hidden = false;
        }
      });
    }

    has(id) {
      return !!id && this.tabs.some((t) => t.dataset.topic === id);
    }

    initialTopic() {
      const wanted = new URLSearchParams(location.search).get('topic');
      if (wanted) {
        const match = this.tabs.find((t) => t.dataset.handle === wanted || t.dataset.topic === wanted);
        if (match) return match.dataset.topic;
      }

      if (this.posted) {
        try {
          const saved = sessionStorage.getItem(this.STORE_KEY);
          if (this.has(saved)) return saved;
        } catch (e) {
          /* ignore */
        }
      }

      const hash = location.hash.replace('#topic-', '');
      if (this.has(hash)) return hash;

      return this.tabs[0].dataset.topic;
    }

    select(id, fromClick) {
      if (!this.has(id)) return;

      this.tabs.forEach((tab) => {
        const on = tab.dataset.topic === id;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.classList.toggle('is-active', on);
        tab.tabIndex = on ? 0 : -1;
      });

      this.panels.forEach((panel) => {
        const on = panel.dataset.topic === id;
        panel.hidden = !on;
        panel.classList.toggle('is-active', on);
      });

      if (!fromClick) return;

      try {
        history.replaceState(null, '', `#topic-${id}`);
      } catch (e) {
        /* ignore */
      }

      /* On mobile the form sits below the list, so bring it into view. */
      if (window.matchMedia('(max-width: 749px)').matches) {
        const panel = this.panels.find((p) => p.dataset.topic === id);
        if (panel) panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    onKeydown(e) {
      const keys = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End'];
      if (!keys.includes(e.key)) return;
      e.preventDefault();

      const i = this.tabs.findIndex((t) => t.dataset.topic === e.target.dataset.topic);
      let next = i;
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = (i + 1) % this.tabs.length;
      else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = (i - 1 + this.tabs.length) % this.tabs.length;
      else if (e.key === 'Home') next = 0;
      else next = this.tabs.length - 1;

      this.select(this.tabs[next].dataset.topic, true);
      this.tabs[next].focus();
    }
  }
  customElements.define('lb-contact-topics', LbContactTopics);
}

/* Product recommendations — async load, rendered with lb-product-card */
function lbLoadRelated() {
  document.querySelectorAll('[data-lb-related]:not([data-lb-loaded])').forEach((el) => {
    const url = el.dataset.url;
    if (!url || url.indexOf('product_id=') === -1) return;
    el.dataset.lbLoaded = '1';
    fetch(url)
      .then((r) => r.text())
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const fresh = doc.querySelector('[data-lb-related]');
        if (fresh) el.innerHTML = fresh.innerHTML;
      })
      .catch(() => {});
  });
}
if (document.readyState !== 'loading') lbLoadRelated();
else document.addEventListener('DOMContentLoaded', lbLoadRelated);

/* Collection sort select — submit on change */
document.addEventListener('change', (e) => {
  const select = e.target.closest('[data-lb-sort]');
  if (select && select.form) {
    if (select.form.requestSubmit) select.form.requestSubmit();
    else select.form.submit();
  }
});

/* Free Gifts (BOGOS) popup must sit ABOVE the modal cart drawer, not behind it.
   The drawer is a modal <dialog> (browser "top layer"), which renders above ALL
   normal content no matter the z-index — so the gift popup was hidden behind it.
   We relocate the popup INTO the open dialog (same top layer, higher z-index) while
   the drawer is open, then move it back home when it closes so the app keeps
   managing it normally. */
(function lbGiftPopupAboveDrawer() {
  const SEL = '.freegifts-main-container.sca-modal-fg';
  const VISIBLE = 'sca-is-visible-fg';

  const modalDialog = () => {
    for (const d of document.querySelectorAll('dialog[open]')) {
      try {
        if (d.matches(':modal')) return d;
      } catch (e) {
        /* older engine — ignore */
      }
    }
    return null;
  };

  let moved = null;
  let home = null;

  const place = () => {
    const gift = document.querySelector(SEL);
    const visible = gift && gift.classList.contains(VISIBLE);
    const dialog = modalDialog();

    if (gift && visible && dialog) {
      if (gift.parentElement !== dialog) {
        if (!home) home = gift.parentElement;
        dialog.appendChild(gift);
        gift.classList.add('lb-gift-over-drawer');
        moved = gift;
      }
    } else if (moved) {
      if (home && moved.parentElement !== home) home.appendChild(moved);
      moved.classList.remove('lb-gift-over-drawer');
      moved = null;
    }
  };

  let scheduled = false;
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      place();
    });
  };

  const start = () => {
    new MutationObserver(schedule).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'open'],
    });
    place();
  };
  if (document.readyState !== 'loading') start();
  else document.addEventListener('DOMContentLoaded', start);
})();

/* External links (social "Follow Us", retailers, rich-text links) open in a new tab.
   Internal links, mailto:/tel:, and anchors that already set target are left alone. */
function lbExternalLinksNewTab(root) {
  (root || document).querySelectorAll('a[href]').forEach((a) => {
    if (a.target) return;
    let url;
    try {
      url = new URL(a.href, window.location.href);
    } catch (e) {
      return;
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return;
    if (!url.hostname || url.hostname === window.location.hostname) return;
    a.target = '_blank';
    a.rel = (a.rel ? a.rel + ' ' : '') + 'noopener noreferrer';
  });
}
if (document.readyState !== 'loading') lbExternalLinksNewTab();
else document.addEventListener('DOMContentLoaded', () => lbExternalLinksNewTab());

/* Keep the mobile cart button clear of any full-width bar pinned to the bottom
   (the Free Gifts "progress" bar, etc.) so they stop overlapping on phones. */
(function lbCartFabClearance() {
  const BAR_SELECTORS = [
    '#PBarNextFrameWrapper',
    '[id*="ProgressBar"]',
    '[class*="progressing-bar"]',
    '[class*="progress-bar-view"]',
  ].join(',');

  const update = () => {
    const fab = document.querySelector('.lb-cart-fab');
    if (!fab) return;
    if (!window.matchMedia('(max-width: 749px)').matches) {
      fab.style.removeProperty('--lb-fab-lift');
      return;
    }
    let lift = 0;
    const vh = window.innerHeight;
    document.querySelectorAll(BAR_SELECTORS).forEach((bar) => {
      if (bar === fab || fab.contains(bar) || bar.contains(fab)) return;
      const s = getComputedStyle(bar);
      if (s.position !== 'fixed' && s.position !== 'sticky') return;
      if (s.display === 'none' || s.visibility === 'hidden') return;
      const r = bar.getBoundingClientRect();
      if (r.height < 6 || r.width < window.innerWidth * 0.7) return;
      // Only lift when the bar is genuinely pinned to the bottom edge AND visible —
      // a bar sitting in-flow below the fold doesn't overlap the fixed FAB.
      if (r.bottom < vh - 4) return; // not resting on the bottom edge
      if (r.top > vh - 4) return; // entirely below the fold
      lift = Math.max(lift, Math.min(r.height, vh - r.top));
    });
    if (lift) fab.style.setProperty('--lb-fab-lift', lift + 12 + 'px');
    else fab.style.removeProperty('--lb-fab-lift');
  };

  const run = () => {
    update();
    // The app injects/updates its bar asynchronously; re-check for a short while.
    let n = 0;
    const t = setInterval(() => {
      update();
      if (++n > 12) clearInterval(t);
    }, 500);
    window.addEventListener('resize', update, { passive: true });
    document.addEventListener('scroll', update, { passive: true, capture: true });
  };

  if (document.readyState !== 'loading') run();
  else document.addEventListener('DOMContentLoaded', run);
})();

/* Cookie consent — themed banner. Remembers the choice and, when Shopify's
   Customer Privacy API is present, records real tracking consent with it. */
(function lbCookieConsent() {
  const init = () => {
    const bar = document.querySelector('[data-lb-cookie]');
    if (!bar) return;
    const KEY = 'lb-cookie-consent';

    const applyConsent = (granted) => {
      try {
        const cp = window.Shopify && window.Shopify.customerPrivacy;
        if (cp && typeof cp.setTrackingConsent === 'function') {
          cp.setTrackingConsent(
            { analytics: granted, marketing: granted, preferences: granted, sale_of_data: granted },
            () => {}
          );
        }
      } catch (e) {
        /* API shape varies by shop config — the stored choice still stands. */
      }
    };

    let saved = null;
    try {
      saved = localStorage.getItem(KEY);
    } catch (e) {
      /* storage blocked — just show the banner each visit */
    }

    if (saved === 'granted' || saved === 'denied') {
      applyConsent(saved === 'granted');
      return;
    }

    bar.hidden = false;
    requestAnimationFrame(() => bar.classList.add('is-in'));

    const choose = (granted) => {
      try {
        localStorage.setItem(KEY, granted ? 'granted' : 'denied');
      } catch (e) {
        /* ignore */
      }
      applyConsent(granted);
      bar.classList.remove('is-in');
      setTimeout(() => {
        bar.hidden = true;
      }, 320);
    };

    bar.querySelector('[data-lb-cookie-accept]')?.addEventListener('click', () => choose(true));
    bar.querySelector('[data-lb-cookie-decline]')?.addEventListener('click', () => choose(false));
  };

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();

/* deploy marker 1787755843 */

/* Lost Boy clips — tap-to-unmute. Browsers only allow sound after a tap, so
   clips and product gallery videos autoplay muted and this toggle turns sound
   on for one video at a time. */
(() => {
  if (window.__lbClipSound) return;
  window.__lbClipSound = true;

  const SCOPE = '.lb-clip, .lb-sound-scope';

  const postToIframe = (iframe, on) => {
    const win = iframe.contentWindow;
    if (!win) return;
    const type = iframe.dataset.videoType;
    if (type === 'youtube') {
      win.postMessage(JSON.stringify({ event: 'command', func: on ? 'unMute' : 'mute', args: '' }), '*');
      if (on) win.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
    } else if (type === 'vimeo') {
      win.postMessage(JSON.stringify({ method: 'setMuted', value: !on }), '*');
      if (on) win.postMessage('{"method":"play"}', '*');
    } else {
      // TikTok embed player
      const send = (msgType) =>
        win.postMessage({ type: msgType, value: null, 'x-tiktok-player': true }, 'https://www.tiktok.com');
      send(on ? 'unMute' : 'mute');
      if (on) send('play');
    }
  };

  const setSound = (scope, on) => {
    const btn = scope.querySelector('[data-lb-sound]');

    // Gallery videos may still be a poster; load the player before unmuting.
    const deferred = scope.querySelector('deferred-media');
    if (on && deferred && !deferred.getAttribute('data-media-loaded') && typeof deferred.showDeferredMedia === 'function') {
      deferred.showDeferredMedia();
    }

    const video = scope.querySelector('video');
    const iframe = scope.querySelector('iframe.lb-clip__embed, iframe[data-video-type]');

    if (video) {
      video.muted = !on;
      if (on) video.play().catch(() => {});
    } else if (iframe) {
      postToIframe(iframe, on);
    }

    if (btn) {
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.setAttribute('aria-label', on ? 'Turn sound off' : 'Turn sound on');
    }
    scope.classList.toggle('is-sound-on', on);
  };

  // Capture on window: the theme's on:click handlers capture on document, so this
  // runs first and stopPropagation keeps the tap from opening the gallery zoom.
  window.addEventListener(
    'click',
    (event) => {
      const btn = event.target.closest('[data-lb-sound]');
      if (!btn) return;
      event.preventDefault();
      event.stopPropagation();

      const scope = btn.closest(SCOPE);
      if (!scope) return;
      const turnOn = btn.getAttribute('aria-pressed') !== 'true';

      // Only one video plays sound at a time.
      if (turnOn) {
        document.querySelectorAll('.is-sound-on').forEach((other) => {
          if (other !== scope && other.matches(SCOPE)) setSound(other, false);
        });
      }
      setSound(scope, turnOn);
    },
    true
  );
})();
